import type { Flow, Question } from '@dashboard/types/config'
import type { FunnelDefinitionNode, FunnelDefinitionRootNode } from '../builder/types/nodes'
import { doesCodeReturnValue, isNodeSetUp } from '../builder/services/flowUtilities'
import { funnelDefinitionEach } from '@dashboard/modules/flow/services/funnelDefinitions'
import { useFlowStore } from '@dashboard/modules/flow/stores/flowStore'
import { useOfferStore } from '@dashboard/modules/offer/stores/offerStore'
import { renderText } from '@dashboard/modules/segment/services/segmentUtilities'
import { useQuestionStore } from '@dashboard/modules/segment/stores/questionStore'
import { useSegmentGroupStore } from '@dashboard/modules/segment/stores/segmentGroupStore'
import { useWidgetStore } from '@dashboard/modules/widget/stores/widgetStore'
import { stripHtml } from '@dashboard/services/string'
import { computed } from 'vue'

export const isEmptyFunnel = (funnelDefinition: FunnelDefinitionRootNode): boolean =>
  funnelDefinition.child.length === 0

export const flowHasQuestions = (funnelDefinition: FunnelDefinitionRootNode) => {
  let hasQuestions = false
  funnelDefinitionEach(funnelDefinition, (node) => {
    if (node.type === 'sg' && node.ask) {
      hasQuestions = true
    }
  })
  return hasQuestions
}

// Re-export from the package as the single source of truth
export { doesCodeReturnValue, isNodeSetUp }

const getNodeSetUpContext = () => ({
  offers: useOfferStore().offers,
  segmentGroups: useSegmentGroupStore().segmentGroups
})

export const doesNodeTerminate = (node: FunnelDefinitionNode): boolean => {
  if (node.type === 'sg' && node.split && Array.isArray(node.split)) {
    if (node.segmentGroupId && node.split.length) {
      return node.split.every((split) => split.child.some((child) => doesNodeTerminate(child)))
    }
    return false
  }
  if (node.type === 'yesno') {
    return node.yes.some((child) => doesNodeTerminate(child)) && node.no.some((child) => doesNodeTerminate(child))
  }
  if (node.type === 'random') {
    return (node.split as Exclude<typeof node.split, false>).every((split) =>
      split.child.some((child) => doesNodeTerminate(child))
    )
  }

  // Code nodes terminate the flow if they return a value
  if (node.type === 'code') {
    return doesCodeReturnValue(node.code)
  }

  return node.type === 'exit' || node.type === 'offer' || node.type === 'redirect' || node.type === 'message'
}

export const questionsInFlow = (funnelDefinition: FunnelDefinitionRootNode) => {
  const questionStore = useQuestionStore()
  let questions: string[] = []
  funnelDefinitionEach(funnelDefinition, (node) => {
    if (node.type === 'sg' && node.ask && node.segmentGroupId) {
      questions.push(node.segmentGroupId)
    }
  })

  return questions
    .map((qId) => {
      // For input questions (raq_*), get by ID directly
      if (qId.startsWith('raq_')) {
        return questionStore.getById(qId)
      }
      // For regular questions linked to dimensions
      return questionStore.getBySegmentGroupId(qId)
    })
    .filter((q) => q !== undefined) // Filter out any undefined results
    .map((q) => ({
      label: stripHtml(renderText(q.text) || ''),
      ...q
    }))
}

export const offersInFlow = (funnelDefinition: FunnelDefinitionRootNode) => {
  const offerStore = useOfferStore()
  let offers: string[] = []
  funnelDefinitionEach(funnelDefinition, (node) => {
    if (node.type === 'offer' && node.offerId) {
      offers.push(node.offerId)
    }
  })

  const OffersGottenById = offers.map((o) => offerStore.getById(o))

  return OffersGottenById.length
    ? OffersGottenById.filter((o) => o !== undefined).map((o) => ({
        label: o.name,
        ...o
      }))
    : []
}

export const redirectsInFlow = (funnelDefinition: FunnelDefinitionRootNode) => {
  let redirects: any[] = []
  funnelDefinitionEach(funnelDefinition, (node) => {
    if (node.type === 'redirect' && node.url !== '') {
      redirects.push(node)
    }
  })
  return redirects
}

export const visibleThingsInFlow = (funnelDefinition: FunnelDefinitionRootNode) => [
  ...questionsInFlow(funnelDefinition),
  ...offersInFlow(funnelDefinition)
]

export const dropdownFlows = computed(() => {
  const flowStore = useFlowStore()
  const flows = flowStore.flows
  return flows
    .map((flow) => ({
      label: flow.name,
      value: flow.id
    }))
    .sort((a, b) => a.label.localeCompare(b.label))
})

export const flowContainsQuestion = (flow: Flow, question: Question) => {
  const questions = questionsInFlow(flow.funnelDefinition)
  return questions.some((q) => q.id === question.id)
}

export const flowErrors = (flow: Flow) => {
  const errors: string[] = []

  // System flows don't need widgets
  if (!flow.isSystemFlow) {
    const widgets = useWidgetStore().getWidgetsForOfferFunnel(flow.id)
    if (widgets.length === 0) {
      errors.push("This flow isn't attached to a widget yet")
    }
  }

  // System flows don't need questions or offers (they can only have Action nodes)
  if (!flow.isSystemFlow) {
    if (offersInFlow(flow.funnelDefinition).length === 0 && questionsInFlow(flow.funnelDefinition).length === 0) {
      if (redirectsInFlow(flow.funnelDefinition).length > 0) {
        errors.push('Heads up: Visitors will be redirected without seeing any content')
      } else {
        errors.push('Add at least one question or offer to get started')
      }
    }
  }

  const context = getNodeSetUpContext()
  funnelDefinitionEach(flow.funnelDefinition, (node) => {
    if (node.type !== 'root' && !isNodeSetUp(node, context)) {
      if (node.type === 'sg' && node.ask && node.segmentGroupId === null) {
        errors.push('Choose which question to ask visitors')
      }
      if (node.type === 'yesno') {
        errors.push('Set up your branching rules')
      }
      if (node.type === 'offer' && node.offerId === null) {
        if (node.desiredType === 'form') {
          errors.push('Select which form to show')
        } else {
          errors.push('Choose what content to display')
        }
      }
      if (node.type === 'redirect' && node.url === '') {
        errors.push('Add the webpage where visitors should go')
      }
      if (node.type === 'code' && node.code === '') {
        errors.push('Add your custom code')
      }
    }
  })
  return errors
}

// Enhanced version that returns error objects with node references
export const flowErrorsWithNodes = (flow: Flow) => {
  const errors: { message: string; node?: FunnelDefinitionNode }[] = []

  // System flows don't need widgets
  if (!flow.isSystemFlow) {
    const widgets = useWidgetStore().getWidgetsForOfferFunnel(flow.id)
    if (widgets.length === 0) {
      errors.push({ message: "This flow isn't attached to a widget yet" })
    }
  }

  // System flows don't need questions or offers (they can only have Action nodes)
  if (!flow.isSystemFlow) {
    if (offersInFlow(flow.funnelDefinition).length === 0 && questionsInFlow(flow.funnelDefinition).length === 0) {
      if (redirectsInFlow(flow.funnelDefinition).length > 0) {
        errors.push({
          message: 'Heads up: Visitors will be redirected without seeing any content'
        })
      } else {
        errors.push({
          message: 'Add at least one question or offer to get started'
        })
      }
    }
  }

  const context = getNodeSetUpContext()
  funnelDefinitionEach(flow.funnelDefinition, (node) => {
    if (node.type !== 'root' && !isNodeSetUp(node, context)) {
      if (node.type === 'sg' && node.ask && node.segmentGroupId === null) {
        errors.push({ message: 'Choose which question to ask visitors', node })
      }
      if (node.type === 'yesno') {
        errors.push({ message: 'Set up your branching rules', node })
      }
      if (node.type === 'offer' && node.offerId === null) {
        if (node.desiredType === 'form') {
          errors.push({ message: 'Select which form to show', node })
        } else {
          errors.push({ message: 'Choose what content to display', node })
        }
      }
      if (node.type === 'redirect' && node.url === '') {
        errors.push({
          message: 'Add the webpage where visitors should go',
          node
        })
      }
      if (node.type === 'code' && node.code === '') {
        errors.push({ message: 'Add your custom code', node })
      }
    }
  })
  return errors
}
